import { use, useMemo } from 'react';
import { getCopy, type CopyFn } from '@packages/copy';
import { AccountContext } from '../lib/account/useAccount';

/**
 * The locale-fixed copy function for the active account's practice type. The
 * backend applies the same catalogs via `getCopy` (invoice PDF, chat glossary);
 * this hook exists so frontend components resolve the account's dialect
 * without prop-drilling the practice type down from the account route.
 *
 * Reads `AccountContext` directly (not `useAccount()`) so the hook works
 * before the account has loaded - `practiceType` falls back to the base
 * (legal) dialect via `getCopy`'s own null handling, matching legacy rows.
 *
 * Memoised on `practiceType`: `i18next`'s `getFixedT` (which `getCopy` calls)
 * builds a fresh closure on every call, so an unmemoised `getCopy(...)` here
 * would return a new `CopyFn` identity on every render regardless of whether
 * the practice type actually changed - silently defeating every call site's
 * `useMemo(() => createXSchema(copy), [copy])` (the zod schema factory
 * pattern in `packages/copy/CLAUDE.md`), which would rebuild its schema (and
 * lose referential stability for `react-hook-form`) on every render instead
 * of only when the resolved dialect changes.
 */
export function useCopy(): CopyFn {
  const accountState = use(AccountContext);
  const practiceType = accountState?.account?.practiceSettings?.practiceType;
  return useMemo(() => getCopy({ practiceType }), [practiceType]);
}
